
(function () {
  'use strict';

  let currentKeyword          = '';
  let currentVariantId        = '';
  let activeIndex             = -1;
  let observer                = null;
  let filterTimer             = null;
  let suppressReset           = false;
  let hideVariantsEnabled     = false;
  let variantAttachedMediaIds = [];

  // ─── Alt text matching ────────────────────────────────────────────────────

  function stripEmoji(str) {
    return str.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
  }

  function extractKeyword(raw) {
    if (!raw) return '';
    const cleaned = stripEmoji(raw.toLowerCase().trim());
    const parts = cleaned.split(/\s*-\s*/);
    return parts[parts.length - 1].trim();
  }

  function altMatches(rawAlt, kw) {
    if (!rawAlt || rawAlt.trim() === '') return true;
    if (!kw) return true;

    const alt = stripEmoji(rawAlt.toLowerCase().trim());
    const k   = stripEmoji(kw.toLowerCase().trim());

    if (!k) return true;
    if (alt === k) return true;

    const escapedKw = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    if (new RegExp('(\\s*-\\s*|\\s+)' + escapedKw + '$').test(alt)) return true;
    if (new RegExp('^\\s*-\\s*' + escapedKw + '$').test(alt)) return true;

    const altStripped = alt.replace(/^[\s\-]+|[\s\-]+$/g, '');
    if (altStripped === k) return true;

    return false;
  }

  // ─── DOM helpers ─────────────────────────────────────────────────────────

  function getGallery()  { return document.querySelector('media-gallery'); }
  function getScroller() { return getGallery()?.querySelector('slideshow-slides'); }

  function getConfig() {
    const gallery = getGallery();
    const el = gallery
      ? gallery.querySelector('script[id^="variant-media-map-"]')
      : document.querySelector('script[id^="variant-media-map-"]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); }
    catch { return {}; }
  }

  function getVariantIdFromUrl() {
    return new URLSearchParams(window.location.search).get('variant') || '';
  }

  function getCheckedVariantId() {
    return document.querySelector(
      'variant-picker input[data-current-checked="true"][data-variant-id]'
    )?.dataset.variantId || '';
  }

  function getAllSlides(gallery) {
    return Array.from(gallery.querySelectorAll('slideshow-slide[data-media-alt]'));
  }

  function getAllThumbs(gallery) {
    return Array.from(gallery.querySelectorAll(
      'slideshow-controls[pagination-position="left"] .slideshow-controls__thumbnail[data-media-alt]'
    ));
  }

  function getVisibleThumbs(gallery) {
    return getAllThumbs(gallery).filter(t => !t.hasAttribute('data-variant-hidden'));
  }

  function parseIndex(el) {
    const m = (el?.getAttribute('on:click') || '').match(/\/select\/(\d+)/);
    return m ? parseInt(m[1], 10) : -1;
  }

  function getMediaId(slide) {
    return slide.querySelector('[data-media-id]')?.dataset.mediaId || '';
  }

  // ─── Keyword resolution ───────────────────────────────────────────────────
  // Called ONLY after morph is complete (post variant:update), so DOM is fresh.

  function resolveKeyword(variantId) {
    const config = getConfig();
    const entry  = config.variantMediaMap?.[variantId];
    if (!entry) return '';

    // entry has { mediaAlt, title } fields
    const mediaAlt     = (entry.mediaAlt || '').toLowerCase().trim();
    const variantTitle = (entry.title    || '').toLowerCase().trim();

    // Prefer title-based keyword (strips emoji, takes last segment after dash)
    const kw = extractKeyword(variantTitle || mediaAlt);
    if (!kw) return '';

    // Verify at least one slide/thumb alt matches — confirms alt-text grouping is set up
    const gallery = getGallery();
    if (!gallery) return '';

    const allAltTexts = [
      ...getAllSlides(gallery).map(s => s.dataset.mediaAlt || ''),
      ...getAllThumbs(gallery).map(t => t.dataset.mediaAlt || '')
    ];

    const hasMatch = allAltTexts.some(alt => alt.trim() !== '' && altMatches(alt, kw));
    return hasMatch ? kw : '';
  }

  // ─── CSS injection ────────────────────────────────────────────────────────

  function injectCSS() {
    if (document.getElementById('vmf-style')) return;
    const s = document.createElement('style');
    s.id = 'vmf-style';
    s.textContent = `
      slideshow-slide[data-variant-hidden="true"] {
        width: 0 !important;
        min-width: 0 !important;
        overflow: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        flex-shrink: 0;
      }
      .slideshow-controls__thumbnail[data-variant-hidden="true"] {
        display: none !important;
      }
    `;
    document.head.appendChild(s);
  }

  // ─── Filter ───────────────────────────────────────────────────────────────

  function shouldShowSlide(slide) {
    const alt     = slide.dataset.mediaAlt || '';
    const mediaId = getMediaId(slide);

    if (!altMatches(alt, currentKeyword)) return false;

    if (hideVariantsEnabled && mediaId) {
      const isAttached = variantAttachedMediaIds.includes(mediaId);
      if (isAttached) {
        if (slide.getAttribute('data-hide-variants-hidden') === 'true') return false;
      }
    }

    return true;
  }

  function applyFilter() {
    const gallery = getGallery();
    if (!gallery) return;

    if (observer) observer.disconnect();

    getAllSlides(gallery).forEach(slide => {
      if (slide.style.display === 'none' && !slide.hasAttribute('data-variant-hidden')) {
        slide.setAttribute('data-hide-variants-hidden', 'true');
      } else if (slide.style.display !== 'none') {
        slide.removeAttribute('data-hide-variants-hidden');
      }
    });

    getAllSlides(gallery).forEach(slide => {
      const show = shouldShowSlide(slide);
      if (show) {
        slide.removeAttribute('data-variant-hidden');
        if (!slide.hasAttribute('data-hide-variants-hidden')) slide.style.display = '';
      } else {
        slide.setAttribute('data-variant-hidden', 'true');
        slide.style.display = '';
      }
    });

    getAllThumbs(gallery).forEach(thumb => {
      const show = altMatches(thumb.dataset.mediaAlt, currentKeyword);
      if (show) {
        thumb.removeAttribute('data-variant-hidden');
        thumb.style.display = '';
      } else {
        thumb.setAttribute('data-variant-hidden', 'true');
      }
    });

    requestAnimationFrame(() => observeGallery(gallery));
  }

  // ─── Scroll / navigation ──────────────────────────────────────────────────

  function scrollToSlide(gallery, absoluteIndex) {
    const slides   = getAllSlides(gallery);
    const target   = slides[absoluteIndex];
    const scroller = getScroller();
    if (!target || !scroller) return;

    activeIndex   = absoluteIndex;
    suppressReset = true;
    clearTimeout(window.__vmfResetTimer);
    window.__vmfResetTimer = setTimeout(() => { suppressReset = false; }, 400);

    scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  function updateThumbActive(gallery, absoluteIndex) {
    getAllThumbs(gallery).forEach(t => {
      t.setAttribute('aria-selected', parseIndex(t) === absoluteIndex ? 'true' : 'false');
    });
  }

  function goToFirstVisible(gallery) {
    const visible = getVisibleThumbs(gallery);
    if (!visible.length) return;
    const idx = parseIndex(visible[0]);
    if (idx < 0) return;
    scrollToSlide(gallery, idx);
    updateThumbActive(gallery, idx);
  }

  function updateGallery(reset) {
    applyFilter();
    requestAnimationFrame(() => {
      const gallery = getGallery();
      if (!gallery) return;
      if (suppressReset) {
        updateThumbActive(gallery, activeIndex);
        return;
      }
      if (reset || activeIndex < 0) {
        goToFirstVisible(gallery);
      }
    });
  }

  // ─── Arrow navigation ─────────────────────────────────────────────────────

  function navigateArrow(gallery, direction) {
    const visible = getVisibleThumbs(gallery);
    if (!visible.length) return;

    let pos = visible.findIndex(t => parseIndex(t) === activeIndex);
    if (pos < 0) pos = 0;

    const next = direction === 'next'
      ? (pos + 1) % visible.length
      : (pos - 1 + visible.length) % visible.length;

    const idx = parseIndex(visible[next]);
    if (idx < 0) return;

    scrollToSlide(gallery, idx);
    updateThumbActive(gallery, idx);
  }

  // ─── Click interceptor ────────────────────────────────────────────────────

  function attachClickInterceptor(gallery) {
    gallery.addEventListener('click', (e) => {

      if (e.target.closest('.slideshow-control--previous')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        navigateArrow(gallery, 'prev');
        return;
      }

      if (e.target.closest('.slideshow-control--next')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        navigateArrow(gallery, 'next');
        return;
      }

      const thumb = e.target.closest(
        'slideshow-controls[pagination-position="left"] .slideshow-controls__thumbnail'
      );
      if (!thumb) return;

      if (thumb.getAttribute('data-variant-hidden') === 'true') {
        e.stopImmediatePropagation();
        e.preventDefault();
        return;
      }

      e.stopImmediatePropagation();
      e.preventDefault();

      const idx = parseIndex(thumb);
      if (idx < 0) return;

      scrollToSlide(gallery, idx);
      updateThumbActive(gallery, idx);

    }, true);
  }

  // ─── Observer ─────────────────────────────────────────────────────────────

  function observeGallery(gallery) {
    if (observer) observer.disconnect();
    observer = new MutationObserver((mutations) => {
      const isMorph = mutations.some(m =>
        Array.from(m.addedNodes).some(n =>
          n instanceof Element && (
            n.hasAttribute('data-media-alt') || n.querySelector('[data-media-alt]')
          )
        )
      );
      if (!isMorph || !currentKeyword) return;
      clearTimeout(filterTimer);
      filterTimer = setTimeout(() => updateGallery(false), 60);
    });
    observer.observe(gallery, { childList: true, subtree: true });
  }

  // ─── Variant change ───────────────────────────────────────────────────────
  // Only called AFTER morph is complete — so resolveKeyword sees the fresh DOM.

  function handleVariantChange(newId) {
    if (!newId) return;
    const id = String(newId);

    currentVariantId = id;
    currentKeyword   = resolveKeyword(id);
    activeIndex      = -1;
    suppressReset    = false;
    clearTimeout(window.__vmfResetTimer);

    updateGallery(true);
  }

  // ─── Event listeners ─────────────────────────────────────────────────────
  //
  // KEY INSIGHT from debug logs:
  //   - `change` fires BEFORE Horizon's fetch/morph → DOM still has old slides
  //     → resolveKeyword returns '' → filtering silently does nothing
  //   - `variant:update` fires AFTER fetch/morph → DOM is fresh → correct
  //   - `variant:update` always has empty e.detail.id, so we read from
  //     getCheckedVariantId() / getVariantIdFromUrl() which are correct by then
  //
  // Solution: ignore `change` for filtering entirely.
  //           Use `variant:update` as the sole trigger — it fires post-morph.

  document.addEventListener('variant:update', (e) => {
    // id is always '' in Horizon's VariantUpdateEvent detail,
    // so fall back to checked input / URL which are updated by this point
    const id =
      String(e.detail?.variant?.id || '') ||
      String(e.detail?.id          || '') ||
      getCheckedVariantId()               ||
      getVariantIdFromUrl();

    if (!id) return;
    handleVariantChange(id);
  });

  const _replaceState = history.replaceState.bind(history);
  history.replaceState = function (...args) {
    _replaceState(...args);
    // replaceState fires before variant:update, so we don't act here —
    // variant:update will handle it with the correct post-morph DOM.
  };

  window.addEventListener('popstate', () => {
    currentVariantId = '';
    suppressReset    = false;
    clearTimeout(window.__vmfResetTimer);
    handleVariantChange(getVariantIdFromUrl() || getCheckedVariantId());
  });

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    injectCSS();

    const config = getConfig();
    hideVariantsEnabled     = config.hideVariantsEnabled || false;
    variantAttachedMediaIds = (config.variantAttachedMediaIds || []).map(String);

    const gallery = getGallery();
    if (!gallery) return;

    attachClickInterceptor(gallery);

    // On page load the DOM is already fully rendered — no morph pending.
    // variant:update won't fire, so we resolve keyword directly here.
    currentVariantId = getVariantIdFromUrl() || getCheckedVariantId();
    currentKeyword   = resolveKeyword(currentVariantId);

    if (currentKeyword) updateGallery(true);

    observeGallery(gallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

// (function () {
//   'use strict';

//   let currentKeyword    = '';
//   let currentVariantId  = '';
//   let activeIndex       = -1;
//   let observer          = null;
//   let filterTimer       = null;
//   let hideVariantsEnabled = false;
//   let variantAttachedMediaIds = [];
//   let isUserClickNavigation = false;

//   // ─── Alt text matching ────────────────────────────────────────────────────

//   function altMatches(rawAlt, kw) {
//     if (!rawAlt || rawAlt.trim() === '') return true;
//     if (!kw) return true;

//     const alt = stripEmoji(rawAlt.toLowerCase().trim());
//     const k   = stripEmoji(kw.toLowerCase().trim());

//     if (!k) return true;
//     if (alt === k) return true;

//     const escapedKw = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

//     if (new RegExp('(\\s*-\\s*|\\s+)' + escapedKw + '$').test(alt)) return true;
//     if (new RegExp('^\\s*-\\s*' + escapedKw + '$').test(alt)) return true;

//     const altStripped = alt.replace(/^[\s\-]+|[\s\-]+$/g, '');
//     if (altStripped === k) return true;

//     return false;
//   }

//   function stripEmoji(str) {
//     return str.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();
//   }

//   function extractKeyword(raw) {
//     if (!raw) return '';
//     const cleaned = stripEmoji(raw.toLowerCase().trim());
//     const parts = cleaned.split(/\s*-\s*/);
//     return parts[parts.length - 1].trim();
//   }

//   // ─── DOM helpers ─────────────────────────────────────────────────────────

//   function getGallery()  { return document.querySelector('media-gallery'); }
//   function getScroller() { return getGallery()?.querySelector('slideshow-slides'); }

//   function getConfig() {
//     const gallery = getGallery();
//     const el = gallery
//       ? gallery.querySelector('script[id^="variant-media-map-"]')
//       : document.querySelector('script[id^="variant-media-map-"]');
//     if (!el) return {};
//     try { return JSON.parse(el.textContent); }
//     catch { return {}; }
//   }

//   function getVariantIdFromUrl() {
//     return new URLSearchParams(window.location.search).get('variant') || '';
//   }

//   function getCheckedVariantId() {
//     return document.querySelector(
//       'variant-picker input[data-current-checked="true"][data-variant-id]'
//     )?.dataset.variantId || '';
//   }

//   function getAllSlides(gallery) {
//     return Array.from(gallery.querySelectorAll('slideshow-slide[data-media-alt]'));
//   }

//   function getAllThumbs(gallery) {
//     return Array.from(gallery.querySelectorAll(
//       'slideshow-controls[pagination-position="left"] .slideshow-controls__thumbnail[data-media-alt]'
//     ));
//   }

//   function getVisibleThumbs(gallery) {
//     return getAllThumbs(gallery).filter(
//       t => !t.hasAttribute('data-variant-hidden')
//     );
//   }

//   function parseIndex(el) {
//     const m = (el?.getAttribute('on:click') || '').match(/\/select\/(\d+)/);
//     return m ? parseInt(m[1], 10) : -1;
//   }

//   function getMediaId(slide) {
//     return slide.querySelector('[data-media-id]')?.dataset.mediaId || '';
//   }

//   // ─── Verify variant title has matching images ─────────────────────────────

//   function variantTitleHasMatchingImages(variantTitle) {
//     if (!variantTitle) return false;
//     const gallery = getGallery();
//     if (!gallery) return false;
//     const keyword = extractKeyword(variantTitle);
//     if (!keyword) return false;
//     const allAltTexts = [
//       ...getAllSlides(gallery).map(s => s.dataset.mediaAlt || ''),
//       ...getAllThumbs(gallery).map(t => t.dataset.mediaAlt || '')
//     ];
//     return allAltTexts.some(alt => {
//       if (!alt || alt.trim() === '') return false;
//       return altMatches(alt, keyword);
//     });
//   }

//   // ─── Resolve keyword for a variant ───────────────────────────────────────

//   function resolveKeyword(variantId) {
//     const config = getConfig();
//     const entry  = config.variantMediaMap?.[variantId];
//     if (!entry) return '';

//     let mediaAlt, variantTitle;
//     if (typeof entry === 'object') {
//       mediaAlt     = (entry.mediaAlt || '').toLowerCase().trim();
//       variantTitle = (entry.title    || '').toLowerCase().trim();
//     } else {
//       mediaAlt     = (entry || '').toLowerCase().trim();
//       variantTitle = '';
//     }

//     if (variantTitle) {
//       if (!variantTitleHasMatchingImages(variantTitle)) return '';
//       return extractKeyword(variantTitle);
//     }

//     const kw = extractKeyword(mediaAlt);
//     if (!kw) return '';
//     if (!variantTitleHasMatchingImages(mediaAlt)) return '';
//     return kw;
//   }

//   // ─── Should a slide be visible? ──────────────────────────────────────────

//   function shouldShowSlide(slide) {
//     const alt     = slide.dataset.mediaAlt || '';
//     const mediaId = getMediaId(slide);

//     if (!altMatches(alt, currentKeyword)) return false;

//     if (hideVariantsEnabled && mediaId) {
//       const isAttachedToAVariant = variantAttachedMediaIds.includes(mediaId);
//       if (isAttachedToAVariant) {
//         const horizonHid = slide.getAttribute('data-hide-variants-hidden') === 'true';
//         if (horizonHid) return false;
//       }
//     }

//     return true;
//   }

//   // ─── CSS injection ────────────────────────────────────────────────────────

//   function injectCSS() {
//     if (document.getElementById('vmf-style')) return;
//     const s = document.createElement('style');
//     s.id = 'vmf-style';
//     s.textContent = `
//       slideshow-slide[data-variant-hidden="true"] {
//         width: 0 !important;
//         min-width: 0 !important;
//         overflow: hidden !important;
//         opacity: 0 !important;
//         pointer-events: none !important;
//         flex-shrink: 0;
//       }
//       .slideshow-controls__thumbnail[data-variant-hidden="true"] {
//         display: none !important;
//       }
//     `;
//     document.head.appendChild(s);
//   }

//   // ─── Core filter ─────────────────────────────────────────────────────────

//   function applyFilter() {
//     const gallery = getGallery();
//     if (!gallery) return;

//     if (observer) observer.disconnect();

//     getAllSlides(gallery).forEach(slide => {
//       if (slide.style.display === 'none' && !slide.hasAttribute('data-variant-hidden')) {
//         slide.setAttribute('data-hide-variants-hidden', 'true');
//       } else if (slide.style.display !== 'none') {
//         slide.removeAttribute('data-hide-variants-hidden');
//       }
//     });

//     getAllSlides(gallery).forEach(slide => {
//       const show = shouldShowSlide(slide);
//       if (show) {
//         slide.removeAttribute('data-variant-hidden');
//         if (!slide.hasAttribute('data-hide-variants-hidden')) {
//           slide.style.display = '';
//         }
//       } else {
//         slide.setAttribute('data-variant-hidden', 'true');
//         slide.style.display = '';
//       }
//     });

//     getAllThumbs(gallery).forEach(thumb => {
//       const show = altMatches(thumb.dataset.mediaAlt, currentKeyword);
//       if (show) {
//         thumb.removeAttribute('data-variant-hidden');
//         thumb.style.display = '';
//       } else {
//         thumb.setAttribute('data-variant-hidden', 'true');
//       }
//     });

//     requestAnimationFrame(() => observeGallery(gallery));
//   }

//   // ─── Scroll to absolute slide index ──────────────────────────────────────

//   function scrollToSlide(gallery, absoluteIndex) {
//     const slides   = getAllSlides(gallery);
//     const target   = slides[absoluteIndex];
//     const scroller = getScroller();
//     if (!target || !scroller) return;
//     scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
//     activeIndex = absoluteIndex;
//   }

//   function updateThumbActive(gallery, absoluteIndex) {
//     getAllThumbs(gallery).forEach(t => {
//       t.setAttribute('aria-selected',
//         parseIndex(t) === absoluteIndex ? 'true' : 'false'
//       );
//     });
//   }

//   function goToFirstVisible(gallery) {
//     const visible = getVisibleThumbs(gallery);
//     if (!visible.length) return;
//     const idx = parseIndex(visible[0]);
//     if (idx < 0) return;
//     scrollToSlide(gallery, idx);
//     updateThumbActive(gallery, idx);
//   }

//   function updateGallery() {
//     applyFilter();
//     if (!isUserClickNavigation) {
//       requestAnimationFrame(() => {
//         const gallery = getGallery();
//         if (gallery) goToFirstVisible(gallery);
//       });
//     }
//   }

//   // ─── Arrow navigation ─────────────────────────────────────────────────────

//   function navigateArrow(gallery, direction) {
//     const visible = getVisibleThumbs(gallery);
//     if (!visible.length) return;

//     let pos = visible.findIndex(t => parseIndex(t) === activeIndex);
//     if (pos < 0) pos = 0;

//     const next = direction === 'next'
//       ? (pos + 1) % visible.length
//       : (pos - 1 + visible.length) % visible.length;

//     const idx = parseIndex(visible[next]);
//     if (idx < 0) return;

//     scrollToSlide(gallery, idx);
//     updateThumbActive(gallery, idx);
//   }

//   // ─── Click interceptor ────────────────────────────────────────────────────

//   function attachClickInterceptor(gallery) {
//     gallery.addEventListener('click', (e) => {

//       if (e.target.closest('.slideshow-control--previous')) {
//         e.stopImmediatePropagation();
//         e.preventDefault();
//         navigateArrow(gallery, 'prev');
//         return;
//       }

//       if (e.target.closest('.slideshow-control--next')) {
//         e.stopImmediatePropagation();
//         e.preventDefault();
//         navigateArrow(gallery, 'next');
//         return;
//       }

//       const thumb = e.target.closest(
//         'slideshow-controls[pagination-position="left"] .slideshow-controls__thumbnail'
//       );
//       if (!thumb) return;

//       if (thumb.dataset.variantHidden === 'true') {
//         e.stopImmediatePropagation();
//         e.preventDefault();
//         return;
//       }

//       e.stopImmediatePropagation();
//       e.preventDefault();

//       const idx = parseIndex(thumb);
//       if (idx < 0) return;

//       isUserClickNavigation = true;
//       scrollToSlide(gallery, idx);
//       updateThumbActive(gallery, idx);
//       setTimeout(() => { isUserClickNavigation = false; }, 200);

//     }, true);
//   }

//   // ─── Observer ─────────────────────────────────────────────────────────────

//   function observeGallery(gallery) {
//     if (observer) observer.disconnect();
//     observer = new MutationObserver((mutations) => {
//       const isMorph = mutations.some(m =>
//         Array.from(m.addedNodes).some(n =>
//           n instanceof Element && (
//             n.hasAttribute('data-media-alt') || n.querySelector('[data-media-alt]')
//           )
//         )
//       );
//       if (!isMorph || !currentKeyword) return;
//       clearTimeout(filterTimer);
//       filterTimer = setTimeout(() => updateGallery(), 60);
//     });
//     observer.observe(gallery, { childList: true, subtree: true });
//   }

//   // ─── Variant change ───────────────────────────────────────────────────────

//   function handleVariantChange(newId, force) {
//     if (!newId) return;
//     const id = String(newId);
//     if (!force && id === currentVariantId) return;
//     currentVariantId      = id;
//     currentKeyword        = resolveKeyword(id);
//     activeIndex           = -1;
//     isUserClickNavigation = false;
//     updateGallery();
//   }

//   // ─── Event listeners ─────────────────────────────────────────────────────

//   document.addEventListener('change', (e) => {
//     const input = e.target;
//     if (!(input instanceof HTMLInputElement) || !input.dataset.variantId) return;
//     handleVariantChange(input.dataset.variantId);
//   });

//   // Horizon's VariantPicker fires VariantUpdateEvent (= 'variant:update') after
//   // EVERY fetch — including fetches triggered by thumbnail image clicks, because
//   // variantChanged() is bound to ALL 'change' events on the picker, not just
//   // variant option radio changes.
//   //
//   // The key distinction: a real variant change carries a NEW variant ID in the
//   // event detail. A thumb-click re-fire carries the same variant ID that's
//   // already selected. We only act if the variant actually changed.
//   document.addEventListener('variant:update', (e) => {
//     const incomingId =
//       String(e.detail?.variant?.id || '') ||
//       String(e.detail?.id || '') ||
//       getCheckedVariantId() ||
//       getVariantIdFromUrl();

//     if (!incomingId) return;

//     // Same variant as already active → this is a post-thumb-click re-fire.
//     // Don't reset navigation or re-run goToFirstVisible.
//     if (incomingId === currentVariantId) return;

//     // Genuinely different variant selected — reset and re-filter.
//     currentVariantId      = incomingId;
//     currentKeyword        = resolveKeyword(incomingId);
//     activeIndex           = -1;
//     isUserClickNavigation = false;
//     updateGallery();
//   });

//   const _replaceState = history.replaceState.bind(history);
//   history.replaceState = function (...args) {
//     _replaceState(...args);
//     handleVariantChange(getVariantIdFromUrl() || getCheckedVariantId());
//   };

//   window.addEventListener('popstate', () => {
//     currentVariantId = '';
//     isUserClickNavigation = false;
//     handleVariantChange(getVariantIdFromUrl() || getCheckedVariantId());
//   });

//   // ─── Init ─────────────────────────────────────────────────────────────────

//   function init() {
//     injectCSS();

//     const config = getConfig();
//     hideVariantsEnabled     = config.hideVariantsEnabled || false;
//     variantAttachedMediaIds = (config.variantAttachedMediaIds || []).map(String);

//     const gallery = getGallery();
//     if (!gallery) return;

//     attachClickInterceptor(gallery);

//     currentVariantId = getVariantIdFromUrl() || getCheckedVariantId();
//     currentKeyword   = resolveKeyword(currentVariantId);

//     if (currentKeyword) updateGallery();

//     observeGallery(gallery);
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', init);
//   } else {
//     init();
//   }

// })();

